/**
 * Tenant public-site API: the menu the header/footer draw, and the full-site
 * toggle. Page/section CRUD is handled inside @kevlarcon/pages against the same
 * axios client, so it isn't duplicated here.
 */
import apiClient from './client';

export type MenuLocation = 'main' | 'footer' | 'footer_2' | 'legal';

export interface MenuItem {
  id: number;
  text: string;
  url: string;
  css_class: string;
  menu_location: MenuLocation;
  order: number;
  /** Library 0.14+ (site/public/menus/): nesting is one level deep. The legacy
   * api/menu/ endpoint does not send these yet. */
  parent?: number | null;
  children?: MenuItem[];
  requires_auth?: boolean;
}

/** Which states get a public page at /pages/state/<abbr>/ — see
 * accounts.models.StatePageVisibility. */
export type StatePageVisibility = 'none' | 'sgo_registered' | 'opted_in' | 'all';

export interface SiteSettings {
  full_site_enabled: boolean;
  state_page_visibility: StatePageVisibility;
}

// Theme colors/fonts live at a separate endpoint now (`site/theme/`, backed by
// `pages.theming`/`ThemeView` — see `@kevlarcon/pages`' `ThemeSettingsDrawer`,
// which fetches/saves them directly rather than through `siteApi`).

/** The header/footer's editable branding fields — see pages.branding and
 * tenant_site.branding_provider.OrganizationBrandingProvider. */
export type TitleTextTransform = '' | 'uppercase' | 'small-caps';

export interface SiteBranding {
  title: string;
  logo: string;
  logo_alt: string;
  header_logo_height: number | null;
  footer_logo: string;
  footer_logo_alt: string;
  footer_logo_height: number | null;
  title_font_size: number | null;
  title_text_transform: TitleTextTransform;
  title_bold: boolean;
  title_italic: boolean;
  title_max_width: number | null;
  footer_heading: string;
  footer_tagline: string;
  footer_doxology: string;
}

/** Maps a `pages.branding.BrandingView` PATCH response (`SiteBranding`'s
 * shape, keyed `title`) onto the platform `TenantBranding` shape (keyed
 * `name`) the header/footer/theme actually read — used by every branding
 * editing surface's `onSaved`, so a save from any one of them (header,
 * footer, or the Brand style modal) fully refreshes what the other two would
 * otherwise keep showing a stale value for. */
export function applyBrandingSave<T extends Record<string, any>>(old: T, saved: Partial<SiteBranding>): T {
  const { title, ...rest } = saved;
  return { ...old, ...(title !== undefined ? { name: title } : {}), ...rest };
}

export const siteApi = {
  getMenu: (location: MenuLocation = 'main'): Promise<MenuItem[]> =>
    apiClient.get('/menu/', { params: { location } }).then((r) => r.data),

  getSettings: (): Promise<SiteSettings> =>
    apiClient.get('/site/settings/').then((r) => r.data),

  updateSettings: (data: Partial<SiteSettings>): Promise<SiteSettings> =>
    apiClient.patch('/site/settings/', data).then((r) => r.data),
};
